// Klaviyo subscribe handler for drlh-email-cta and email-cta-inline snippets.
// Intercepts [data-klaviyo-subscribe] forms, POSTs to Klaviyo v3 subscriptions API.
// Reads data-public-key (company_id) and data-list-id from the form element.
//
// The <script src> for this file is emitted from four places (drlh-email-cta,
// drlh-article, drlh-thank-you, email-cta-inline). Two of them on one page ran
// this IIFE twice, which bound the document submit listener twice and made every
// subscribe POST twice. Guarded the same way as assets/drlh-parallax.js.
(function () {
  'use strict';

  if (window.__drlhKlaviyoInit) return;
  window.__drlhKlaviyoInit = true;

  // A static asset cannot call the `t` filter, so the four user-facing strings
  // are rendered onto the form as data-msg-* by whichever Liquid emitted it
  // (general.newsletter.invalid / .not_configured / .error / .network_error).
  // These constants are the last-resort fallback for a form that predates the
  // attributes; they are English and are NOT what an RU visitor should see. If
  // one of them ever shows up in RU, the form is missing its data-msg-*.
  var FALLBACK = {
    invalid: 'Please enter a valid email address.',
    notConfigured: 'Sign-up is not set up right now. Please contact us directly.',
    error: 'Something went wrong. Please try again.',
    network: 'Network error. Please check your connection and try again.'
  };

  function msg(form, name) {
    return (form && form.dataset['msg' + name.charAt(0).toUpperCase() + name.slice(1)]) || FALLBACK[name];
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-klaviyo-subscribe]');
    if (!form) return;
    e.preventDefault();

    var publicKey = form.dataset.publicKey;
    var listId = form.dataset.listId;
    var emailInput = form.querySelector('[type="email"]');
    var submitBtn = form.querySelector('[type="submit"]');
    var formRow = form.querySelector('.drlh-email-cta__form');
    var successEl = form.querySelector('.drlh-email-cta__success');
    var errorEl = form.querySelector('.drlh-email-cta__errors');

    hideEl(errorEl);

    if (!emailInput || !emailInput.checkValidity()) {
      showError(errorEl, msg(form, 'invalid'));
      return;
    }

    if (!publicKey || !listId) {
      showError(errorEl, msg(form, 'notConfigured'));
      return;
    }

    var email = emailInput.value.trim();
    // Site locale (en / ru) → stored as drlh_lang so Klaviyo can route the
    // welcome flow + lead magnet by language, not just country. Sourced from
    // <html lang> which theme.liquid sets to request.locale.iso_code.
    var lang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    var profileAttrs = { email: email };
    var props = {};
    if (lang) props.drlh_lang = lang;
    // The guide this signup earns, as a LIVE cdn url resolved by Liquid at page
    // render (snippets/drlh-guide-pdf-attr.liquid reads the theme setting that
    // `pnpm guides:publish` rewrites on every upload).
    //
    // Without this the welcome email fell back to a url frozen into the Klaviyo
    // template, which 404s the moment a guide is republished and Shopify mints a
    // new filename. The guide-gate path (assets/klaviyo-newsletter.js) has always
    // sent this; these forms did not, which is why only some subscribers hit the
    // dead link. Same property name, so one email template serves both paths.
    var guidePdf = form.dataset.guidePdf;
    if (guidePdf) props.drlh_guide_pdf = guidePdf;
    if (Object.keys(props).length) profileAttrs.properties = props;
    // A form without a submit button is valid markup (implicit submit on Enter),
    // so never assume one is there.
    var originalLabel = submitBtn ? submitBtn.textContent : '';
    setButton(submitBtn, true, '…'); // ellipsis

    fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(publicKey),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttrs
                }
              }
            },
            relationships: {
              list: {
                data: { type: 'list', id: listId }
              }
            }
          }
        })
      }
    )
      .then(function (res) {
        if (res.ok || res.status === 202) {
          if (formRow) formRow.hidden = true;
          showEl(successEl);
          // Stash the email (session-scoped) so the thank-you quiz can PATCH its
          // preference answers (drlh_country / drlh_interests) onto this same
          // Klaviyo profile. Read + cleared by drlh-thank-you.liquid; also dropped
          // when the tab closes. Without this the quiz has no identity and no-ops.
          try { sessionStorage.setItem('drlh_email', email); } catch (err) {}
          // Redirect to thank-you page if data-redirect is set
          var redirect = form.dataset.redirect;
          if (redirect) {
            startRedirect(successEl, redirect);
          }
        } else {
          return res.json().then(function (body) {
            var detail = body && body.errors && body.errors[0] && body.errors[0].detail;
            showError(errorEl, detail || msg(form, 'error'));
            setButton(submitBtn, false, originalLabel);
          });
        }
      })
      .catch(function () {
        showError(errorEl, msg(form, 'network'));
        setButton(submitBtn, false, originalLabel);
      });
  });

  // Success note + redirect countdown.
  //
  // The confirmation used to be a line of grey text that vanished a beat later
  // when the page changed under the reader. It is now a padded, tinted note with
  // a ring that fills over the wait, so the jump is announced rather than
  // sprung. The ring is decorative (aria-hidden); the note itself keeps whatever
  // role the Liquid gave it, so assistive tech still announces the words.
  //
  // REDIRECT_MS is the single source for both the animation and the navigation:
  // they cannot drift apart. Same code path on phones and desktop, so a mobile
  // visitor gets the identical 3s wait and the same window.location.href jump.
  var REDIRECT_MS = 3000;

  function startRedirect(el, url) {
    if (el) {
      el.classList.add('drlh-signup-note');
      // prefers-reduced-motion: no sweeping ring, but the same wait, so nobody
      // is navigated away mid-sentence.
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!still && !el.querySelector('.drlh-signup-note__ring')) {
        var ring = document.createElement('span');
        ring.className = 'drlh-signup-note__ring';
        ring.setAttribute('aria-hidden', 'true');
        ring.style.setProperty('--drlh-redirect-ms', REDIRECT_MS + 'ms');
        el.insertBefore(ring, el.firstChild);
      }
    }
    setTimeout(function () { window.location.href = url; }, REDIRECT_MS);
  }

  function setButton(btn, disabled, label) {
    if (!btn) return;
    btn.disabled = disabled;
    btn.textContent = label;
  }
  function showEl(el) { if (el) el.hidden = false; }
  function hideEl(el) { if (el) el.hidden = true; }
  function showError(el, text) {
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
  }
})();
